import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Radio,
  Switch,
  InputNumber,
  DatePicker,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./style.css";

const { TextArea } = Input;

const MULTI_INPUT_STATUS = {
  checking: { text: "Checking...", className: "checking" },
  valid: { text: "Valid API key", className: "valid" },
  invalid: { text: "Invalid API key", className: "invalid" },
  ambiguous: {
    text: "Couldn't verify this key right now",
    className: "warning",
  },
};

const MultiInput = ({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  addLabel,
  validateItem,
}) => {
  const [rows, setRows] = useState(() =>
    Array.isArray(value) && value.length ? value : [""],
  );
  const [statuses, setStatuses] = useState({});

  const checkRow = async (row) => {
    const key = (row || "").trim();
    if (!validateItem || !key) return;
    setStatuses((prev) =>
      prev[key] && prev[key] !== "ambiguous"
        ? prev
        : { ...prev, [key]: "checking" },
    );
    const result = await validateItem(key);
    if (result) setStatuses((prev) => ({ ...prev, [key]: result }));
  };

  useEffect(() => {
    const incoming = Array.isArray(value) ? value : [];
    const current = rows.map((row) => row.trim()).filter(Boolean);
    if (JSON.stringify(incoming) !== JSON.stringify(current)) {
      setRows(incoming.length ? incoming : [""]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (nextRows) => {
    setRows(nextRows);
    onChange?.(nextRows.map((row) => row.trim()).filter(Boolean));
  };

  if (disabled) {
    const saved = rows.filter((row) => row.trim());
    return (
      <div className="multi-input">
        {saved.length ? (
          saved.map((row, index) => (
            <Input key={index} value={row} disabled />
          ))
        ) : (
          <Input value="-" disabled />
        )}
      </div>
    );
  }

  return (
    <div className="multi-input">
      {rows.map((row, index) => {
        const status = MULTI_INPUT_STATUS[statuses[row.trim()]];
        return (
          <div className="multi-input__item" key={index}>
            <div className="multi-input__row">
              <Input
                value={row}
                placeholder={placeholder}
                status={status?.className === "invalid" ? "error" : undefined}
                onChange={(e) =>
                  emit(
                    rows.map((item, i) => (i === index ? e.target.value : item)),
                  )
                }
                onBlur={() => {
                  checkRow(row);
                  onBlur?.();
                }}
              />
              {rows.length > 1 && (
                <DeleteOutlined
                  className="multi-input__remove"
                  onClick={() => {
                    emit(rows.filter((_, i) => i !== index));
                    setTimeout(() => onBlur?.(), 0);
                  }}
                />
              )}
            </div>
            {status && (
              <div
                className={`multi-input__status multi-input__status--${status.className}`}
              >
                {status.text}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="multi-input__add"
        onClick={() => setRows([...rows, ""])}
      >
        <PlusOutlined /> {addLabel || "Add another"}
      </button>
    </div>
  );
};

const InputCard = ({ title, fields = [], children }) => {
  const renderField = (field) => {
    switch (field.type) {
      case "switch":
        return <Switch />;
      case "percentage":
        return (
          <InputNumber
            placeholder={field.placeholder}
            min={0}
            max={100}
            style={{ width: "100%" }}
            addonAfter="%"
          />
        );
      case "password":
        return (
          <Input.Password
            placeholder={field.placeholder}
            visibilityToggle={true}
          />
        );
      case "segmented":
        return (
          <Radio.Group
            options={field.options}
            optionType="button"
            buttonStyle="solid"
            className="custom-segmented-toggle"
          />
        );
      case "radio":
        return (
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            className="custom-segmented-control"
            options={field.options || []}
          />
        );
      case "standard-radio":
        return (
          <Radio.Group className="native-radio-group">
            {(field.options || []).map((opt) => (
              <Radio key={opt.value} value={opt.value}>
                {opt.label}
              </Radio>
            ))}
          </Radio.Group>
        );
      case "input-number":
        return (
          <InputNumber
            placeholder={field.placeholder}
            min={field.min ?? 0}
            style={{ width: "100%" }}
          />
        );
      case "currency-input":
        return (
          <InputNumber
            placeholder={field.placeholder}
            min={field.min ?? 0}
            style={{ width: "100%" }}
            addonAfter="$"
          />
        );
      case "textarea":
        return (
          <TextArea
            rows={field.rows || 4}
            placeholder={field.placeholder}
            style={field.customStyle || { width: "100%" }}
          />
        );
      case "currency":
        return <Input disabled placeholder={field.placeholder} />;
      case "date":
        return (
          <DatePicker
            placeholder={field.placeholder || "Select Date"}
            style={{ width: "100%" }}
            format={field.format || "MM/DD/YYYY"}
          />
        );
      case "select":
        return (
          <Select
            placeholder={field.placeholder}
            options={(field.options || []).map((opt) =>
              typeof opt === "string" ? { label: opt, value: opt } : opt,
            )}
            onFocus={field.onFocus}
            loading={field.loading}
            showSearch={field.showSearch}
            optionFilterProp="label"
            notFoundContent={field.notFoundContent}
          />
        );
      case "phone":
        return (
          <PhoneInput
            country="jm"
            inputClass="phone-input"
            placeholder={field.placeholder}
          />
        );
      case "multi-input":
        return (
          <MultiInput
            placeholder={field.placeholder}
            addLabel={field.addLabel}
            validateItem={field.validateItem}
          />
        );
      case "tags":
        return (
          <Select
            mode="tags"
            style={{ width: "100%" }}
            placeholder={field.placeholder}
            tokenSeparators={[","]}
            suffixIcon={null}
            open={false}
          />
        );
      default:
        return (
          <Input
            type={field.type || "text"}
            placeholder={field.placeholder}
            {...(field.type === "number" && {
              min: field.min ?? 0,
              onKeyDown: (e) => {
                if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
              },
            })}
            prefix={field.prefix}
            addonAfter={field.addonAfter}
          />
        );
    }
  };

  return (
    <div className="input-card">
      <div className="card-title">{title}</div>
      <div className="fields-container">
        {fields.map((field) => {
          const isSwitch = field.type === "switch";
          const isNumber = field.type === "input-number";
          const isSegmented = field.type === "segmented";

          return (
            <div
              key={field.name}
              className={`field-wrapper ${field.fullWidth ? "full-width" : ""} ${field.className || ""}`}
            >
              {isSegmented ? (
                <div className="segmented-card-layout">
                  <div className="segmented-info">
                    <span className="segmented-label-tag">{field.label}</span>
                    {field.description && (
                      <p className="segmented-description-tag">
                        {field.description}
                      </p>
                    )}
                  </div>
                  <Form.Item
                    name={field.name}
                    rules={field.rules || []}
                    noStyle
                  >
                    {renderField(field)}
                  </Form.Item>
                </div>
              ) : isSwitch ? (
                <div className="switch-field-wrapper">
                  <div className="switch-info">
                    <span className="switch-main-label">{field.label}</span>
                    {field.description && (
                      <p className="switch-description">{field.description}</p>
                    )}
                  </div>
                  <Form.Item
                    name={field.name}
                    valuePropName="checked"
                    initialValue={false}
                    rules={field.rules || []}
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                </div>
              ) : isNumber ? (
                <div className="number-field-inner">
                  <span className="number-label">{field.label}</span>
                  <div className="number-input-group">
                    <span className="suffix-text">
                      {field.suffix || "Days"}
                    </span>
                    <Form.Item
                      name={field.name}
                      rules={field.rules || []}
                      noStyle
                    >
                      <InputNumber
                        placeholder={field.placeholder}
                        min={field.min ?? 0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </div>
                </div>
              ) : (
                <Form.Item
                  name={field.name}
                  label={field.label}
                  rules={field.rules || []}
                  initialValue={field.type === "currency" ? "JMD" : undefined}
                  extra={field.extra}
                  validateTrigger={field.validateTrigger}
                >
                  {field.disabled
                    ? React.cloneElement(renderField(field), { disabled: true })
                    : renderField(field)}
                </Form.Item>
              )}
            </div>
          );
        })}
      </div>

      {children}
    </div>
  );
};

export default InputCard;
